// What the far canopy looks like on the ground. The lookup is canopyClip.ts; the rasters say how much leaf there is, how tall, how wide the woody crowns are, how lush it is and which forms it is made of; the
// colours and the cover law are the artist's (the server's ecology_look.json, carried by every window: uniforms `uCPal`, `uCK`, `uCGap`), so nothing about the world's palette is written here. Design:
// claude/ecology-lod-design.md.
//
// The raster is far coarser than a crown (10 m to 1.3 km between vertices; a shrub is 2 m, a tree 10 m), so the crowns themselves are made here, in world space, from the crown width and the cover: a lattice of
// cells one crown wide, a crown in each cell with the probability that gives the cover, a lumpy dome with its own size, colour and a lit side, leaf clumps on it, and the ground darker round its foot. While a
// pixel is much smaller than a crown the pattern is what you see; as the pixel grows to the crown's size the pattern fades into the mean (cover, mean colour), so the ground changes into a smooth tint with distance
// instead of shimmering. The crown width is quantised to octaves of the lattice (the lattice never slides as the data under it changes) and two octaves are cross-faded in between.
//
// Two layers: the grass and ferns are a tint over the ground (their tufts are far smaller than a pixel from this far), the trees and shrubs are the crowns.
//
// Nothing here knows about the ground's colour: `canopyPaint` takes the colour the bare ground has and gives back the colour with the canopy over it.
import { CANOPY_GLSL } from './canopyClip';

/** The fraction of a cell one crown covers when it is there at the sparsest settings (crowns are thinned out below this cover, grown above it). */
const CROWN_FILL = 0.62;

export const CANOPY_LOOK_GLSL = /* glsl */`
${CANOPY_GLSL}
float cnHash(vec2 p) {
  vec3 p3 = fract(vec3(p.xyx) * 0.1031);
  p3 += dot(p3, p3.yzx + 33.33);
  return fract((p3.x + p3.y) * p3.z);
}
vec4 cnHash4(vec2 p) { return vec4(cnHash(p), cnHash(p + 17.31), cnHash(p + 41.07), cnHash(p + 73.19)); }
float cnNoise(vec2 p) {
  vec2 i = floor(p), f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  return mix(mix(cnHash(i), cnHash(i + vec2(1.0, 0.0)), f.x), mix(cnHash(i + vec2(0.0, 1.0)), cnHash(i + vec2(1.0, 1.0)), f.x), f.y);
}

// the colour of a form (0 tree, 1 shrub, 2 fern, 3 grass) from shaded (lit = 0) to lit (lit = 1), for tone 0 (dry) to 1 (lush): the artist's palette
vec3 cnForm(int f, float lit, float tone) {
  int b = f * 4;
  return mix(mix(uCPal[b], uCPal[b + 1], lit), mix(uCPal[b + 2], uCPal[b + 3], lit), tone);
}

// Crowns on a lattice of cells 'cell' metres wide (world position p): a crown in a cell with probability pres, a lump round a point jittered off the cell's middle, of radius rr cells (0.6 to 1.3 of it, by
// crown), its edge pushed in and out by a noise. The four cells whose middles surround p are the only ones that can reach it. edge is the soft edge's width in cells (a pixel). Returns x: how much of the
// pixel the nearest crown covers, y: the distance to the nearest crown in its radii (below 1 inside it), z: a number for the crown, 0 to 1. nv is the covering crown's offset from its middle in radii (east, south).
vec4 cnCrowns(vec2 p, float cell, float pres, float rr, float edge, out vec2 nv) {
  vec2 q = p / cell;
  q += 0.3 * (vec2(cnNoise(q * 1.3), cnNoise(q * 1.3 + 9.1)) - 0.5);                  // a warp, so the lattice does not show
  vec2 i0 = floor(q - 0.5);
  pres = clamp(pres * (0.35 + 1.3 * cnNoise(q / 6.0 + 3.1)), 0.0, 1.0);                // crowns come in clumps and clearings (the noise's mean is a half, so the cover is kept)
  float cov = 0.0, dmin = 9.0, id = 0.0;
  nv = vec2(0.0);
  for (int a = 0; a < 4; a++) {
    vec2 ij = i0 + vec2(float(a & 1), float(a >> 1));
    vec4 h = cnHash4(ij);
    if (h.x >= pres) continue;
    vec2 c = ij + 0.5 + (h.yz - 0.5) * 0.7;
    float r = rr * (0.6 + 0.7 * h.w);
    vec2 dv = (q - c) / r;
    float lump = 1.0 + 0.7 * (cnNoise(dv * 2.1 + h.zw * 31.0) - 0.5);                  // the edge in and out by a third
    float d = length(dv) / lump;
    dmin = min(dmin, d);
    float e = min(edge / r, 0.45);
    float m = 1.0 - smoothstep(1.0 - e, 1.0 + e, d);
    if (m > cov) { cov = m; nv = dv / lump; id = cnHash(ij + 5.7); }
  }
  return vec4(cov, dmin, id, 0.0);
}

// The crowns at world position pp for crowns w metres wide: the lattice quantised to octaves of w (so it never slides as w changes) with two octaves cross-faded between 0.35 and 0.65 of the way up one.
vec4 cnLayer(vec2 pp, float w, float pres, float rr, float fp, out vec2 nv) {
  float e = log2(w);
  float fl = floor(e), f = e - fl;
  float c0 = exp2(fl);
  if (f < 0.35) return cnCrowns(pp, c0, pres, rr, fp / c0, nv);
  if (f > 0.65) return cnCrowns(pp, 2.0 * c0, pres, rr, fp / (2.0 * c0), nv);
  vec2 nva, nvb;
  vec4 a = cnCrowns(pp, c0, pres, rr, fp / c0, nva);
  vec4 b = cnCrowns(pp, 2.0 * c0, pres, rr, fp / (2.0 * c0), nvb);
  float t = smoothstep(0.35, 0.65, f);
  nv = t > 0.5 ? nvb : nva;
  return vec4(mix(a.x, b.x, t), mix(a.y, b.y, t), t > 0.5 ? b.z : a.z, 0.0);
}

// The ground colour 'ground' with the canopy over it, at world position p (rel: the same counted from the floating origin), dist metres from the eye, fp metres per pixel on the ground, land 0 to 1 (0 under
// the sea), sunW the direction to the sun (world, unit). cover is how much of the ground the canopy hides, 0 to 1 (for the specular and the roughness). nTS is the tilt the crowns give the lit normal
// (the ground's tangent frame: x east, y north, z up; 0, 0, 1 where there is none), so the real sun and sky light the crowns as domes, and the shade of a crown falls on the ground away from the sun.
vec3 canopyPaint(vec3 ground, vec2 p, vec2 rel, float dist, float fp, float land, vec3 sunW, out float cover, out vec3 nTS) {
  cover = 0.0;
  nTS = vec3(0.0, 0.0, 1.0);
  float fade = smoothstep(uCFade.x, uCFade.y, dist) * land * uCOn;
  if (fade < 0.002) return ground;
  vec4 A, B;
  canopyAt(rel, dist, A, B);
  float L = A.x;
  if (L < 0.004) return ground;
  float iL = 1.0 / L;
  float Lt = A.w, Ls = B.x, Lf = B.y, Lg = B.z;
  float Lw = Lt + Ls, Lu = Lf + Lg;
  float hgt = max(A.y * iL, 0.05);
  // how lush it is: the artist's tone, with patches of wetter and drier ground a few tens of metres across (faded out where a pixel is that big)
  float tone0 = clamp(B.w * iL, 0.0, 1.0);
  float tone = clamp(tone0 + 0.4 * (cnNoise(p / 45.0 + 7.7) - 0.5) * (1.0 - smoothstep(8.0, 40.0, fp)), 0.0, 1.0);

  // detail below the raster's own cells: the cover varies over tens of metres (faded out where a pixel is that big), around the raster's mean
  float mv = 1.0 + 0.5 * (cnNoise(p / 90.0) - 0.5) * (1.0 - smoothstep(10.0, 40.0, fp)) + 0.4 * (cnNoise(p / 28.0 + 3.3) - 0.5) * (1.0 - smoothstep(3.0, 12.0, fp));

  // the ground layer: the grass and fern tint, patchy over a few metres
  vec3 g1 = ground;
  float covU = 1.0 - exp(-uCK * Lu * mv);
  if (Lu > 0.002) {
    vec3 gcol = (Lg * cnForm(3, 0.55, tone) + Lf * cnForm(2, 0.55, tone)) / Lu;
    float pat = 0.86 + 0.28 * cnNoise(p / 7.0);
    pat *= mix(0.92 + 0.16 * cnNoise(p / 1.9), 1.0, smoothstep(0.4, 1.5, fp));
    g1 = mix(ground, gcol * pat, covU);
  }

  // the woody layer: crowns while a pixel is well under one crown wide, their mean beyond
  float covW = 1.0 - exp(-uCK * Lw * mv);
  vec3 col = g1;
  float mask = 0.0;
  if (Lw > 0.002) {
    float st = Lt / Lw;
    float w = max(A.z / Lw, 0.3);
    vec3 mean = st * cnForm(0, 0.55, tone) + (1.0 - st) * cnForm(1, 0.55, tone);
    vec3 underMean = g1 * mix(1.0, uCGap, covW);
    float vis = 1.0 - smoothstep(0.3, 0.9, fp / w);
    mask = covW;
    vec3 leaf = mean;
    vec3 under = underMean;
    if (vis > 0.01) {
      float pres = clamp(covW / ${CROWN_FILL.toFixed(2)}, 0.0, 1.0);
      float rr = sqrt(min(covW / max(pres, 1.0e-3), 0.95) / 3.14159);
      vec2 nv;
      vec4 cr = cnLayer(p, w, pres, rr, fp, nv);
      int form = cr.z < st ? 0 : 1;
      float d2 = min(dot(nv, nv), 1.0);
      // leaf clumps over the dome: two scales of the crown (a third and a tenth of its radius), faded out as a pixel gets bigger than they are
      float clump = 0.5 * cnNoise(nv * 3.2 + cr.z * 50.0) + 0.5 * cnNoise(nv * 9.0 + cr.z * 90.0);        // in the crown's own coordinates: a world position this large loses the hash's precision and shows its stripes
      float cf = 1.0 - smoothstep(0.04, 0.16, fp / w);
      float lit = clamp(0.62 + (clump - 0.5) * 0.9 * cf - 0.3 * d2, 0.0, 1.0);                             // the sun does the dome's shading (nTS); this is the leaves' own light and dark, and the rim's occlusion
      float ct = clamp(tone + 0.3 * (fract(cr.z * 13.1) - 0.5), 0.0, 1.0);                 // this crown is a little drier or wetter than the ground round it
      vec3 crown = cnForm(form, lit, ct) * (0.88 + 0.24 * fract(cr.z * 7.3));
      // the ground at the crowns' feet is in their shade, and a crown's shadow falls away from the sun: the ground is shaded where a crown stands on the sun's side of it, hgt up
      float foot = 1.0 - smoothstep(1.0, 2.0, cr.y);
      float shade = 0.0;
      if (sunW.y > 0.05) {
        vec2 off = sunW.xz / max(sunW.y, 0.2) * (0.7 * hgt);
        vec2 nvs;
        vec4 cs = cnLayer(p + off, w, pres, rr, fp, nvs);
        shade = cs.x * (1.0 - cr.x);
      }
      under = g1 * mix(1.0, uCGap, foot) * (1.0 - 0.5 * shade);
      mask = mix(covW, cr.x, vis);
      leaf = mix(mean, crown, vis);
      under = mix(underMean, under, vis);
      // the dome's normal: outward from the crown's middle, flattened a little
      float nz = sqrt(max(1.0 - d2, 0.0));
      vec3 dome = normalize(vec3(nv.x * 0.9, -nv.y * 0.9, max(nz, 0.15)));                  // east, north (nv's second axis is south), up
      nTS = normalize(mix(vec3(0.0, 0.0, 1.0), dome, vis * cr.x * (1.0 - smoothstep(0.7, 1.0, d2) * 0.5)));
    }
    col = mix(under, leaf, mask);
  }
  cover = (1.0 - (1.0 - covU) * (1.0 - mask)) * fade;
  nTS = normalize(mix(vec3(0.0, 0.0, 1.0), nTS, fade));
  return mix(ground, col, fade);
}
`;
